import { useTranslation } from "react-i18next";
// src/pages/spending/SpendingFixedAssetsList.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiGet, apiPost } from "../../api/client";
import { companyRoute } from "../../utils/routeHelpers";
import {
  ListToolbar,
  MenuItem,
  SortOption,
} from "../../components/ListToolbar";
import EmptyState from "../../components/EmptyState";
import { StatusBubble } from "../../components/StatusBubble";
import {
  parseYmdLocal,
  ymdTodayLocal,
  formatAnyDateToLocale,
} from "../../utils/dateUtils";
import { formatNumber } from "../../utils/formatNumber";
import { A } from "../../styles/appleClasses";
import { Button } from "../../components/Button";
import {
  isClosedPeriodError,
} from "../../utils/closedPeriod";

type FixedAsset = {
  id: number;
  name: string;
  asset_account_id?: number | null;
  asset_account_name?: string | null;
  disposed?: boolean;
  on_account?: boolean;
  amount_paid?: number | null;
  amount_due?: number | null;
  payment_status?: string | null;
};

type Schedule = {
  id: number;
  start_date: string; // YYYY-MM-DD
  useful_life_months: number;
  status: string; // active | closed | voided
};

type Position = {
  cost: number;
  accum_depr: number;
  nbv: number;
  has_active_schedule: boolean;
};

type Row = {
  id: number;
  name: string;
  category?: string | null;
  start_date?: string | null; // purchase date
  end_date?: string | null;
  cost?: number | null;
  nbv?: number | null;
  status: "Active" | "Closed" | "No Schedule" | "Disposed";
  on_account?: boolean;
  amount_due?: number | null;
  payment_status?: string | null;
};

type DepreciationSummary = {
  posted_through: string | null;
  latest_available_through: string;
};

type SortKey = "start" | "name" | "cost" | "nbv" | "status";

type BubbleType =
  | "preorder"
  | "fulfilled"
  | "paid"
  | "partial"
  | "unpaid"
  | "voided"
  | "open";

type DotColor = "red" | "yellow" | "green" | "grey" | undefined;

function downloadCsv(
  filename: string,
  headers: string[],
  rows: (string | number)[][],
) {
  const esc = (v: any) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv =
    [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join(
      "\n",
    ) + "\n";

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const fmtDate = (input?: string | null) => formatAnyDateToLocale(input);

function money(n?: number | null) {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  const s = formatNumber(n);
  return s.includes("$") ? s : `$${s}`;
}

const lower = (s?: string | null) =>
  String(s || "")
    .toLowerCase()
    .trim();

function getStatusBubbleProps(row: Row): {
  label: string;
  bubbleType: BubbleType;
  showDot: boolean;
  dotColor?: DotColor;
} {
  let bubbleType: BubbleType = "open";

  if (row.status === "Active") bubbleType = "open";
  else if (row.status === "Closed") bubbleType = "fulfilled";
  else if (row.status === "Disposed") bubbleType = "voided";
  else bubbleType = "preorder"; // No Schedule

  const ps = lower(row.payment_status);
  const due = Number(row.amount_due || 0);

  if (row.on_account) {
    if (ps === "partial") {
      return {
        label: row.status,
        bubbleType,
        showDot: true,
        dotColor: "yellow",
      };
    }

    if (ps === "unpaid" || due > 0) {
      return {
        label: row.status,
        bubbleType,
        showDot: true,
        dotColor: "red",
      };
    }
  }

  return {
    label: row.status,
    bubbleType,
    showDot: false,
  };
}

const addMonthsYmdLocal = (startYmd: string, addMonths: number) => {
  const d0 = parseYmdLocal(startYmd);
  if (!d0) return "";
  const d = new Date(d0.getTime());
  d.setMonth(d.getMonth() + addMonths);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const ROWS_PER_PAGE = 10;

type DisplayError =
  | { kind: "raw"; text: string }
  | { kind: "load" | "record" | "closed" };

export default function SpendingFixedAssetsList() {
  const { t } = useTranslation();
  const errorText = (error: DisplayError) => {
    if (error.kind === "raw") return error.text;
    if (error.kind === "load") return t("spendingFixedAssets.errors.load");
    if (error.kind === "record") return t("spendingFixedAssets.errors.record");
    return t("sales.errors.closedSubmit", { date: t("sales.errors.closedDate") });
  };
  const statusLabel = (status: Row["status"]) => {
    switch (status) {
      case "Active": return t("spendingFixedAssets.status.active");
      case "Closed": return t("spendingFixedAssets.status.closed");
      case "Disposed": return t("spendingFixedAssets.status.disposed");
      case "No Schedule": return t("spendingFixedAssets.status.noSchedule");
    }
  };
  const { company_id = "" } = useParams<{ company_id: string }>();
  const navigate = useNavigate();
  const go = (path: string) => navigate(companyRoute(company_id, path));

  const getC = async <T,>(path: string): Promise<T> => {
    if (!company_id) return [] as unknown as T;
    const sep = path.includes("?") ? "&" : "?";
    return apiGet<T>(`${path}${sep}company_id=${company_id}`);
  };

  const collator = useMemo(
    () => new Intl.Collator(undefined, { numeric: true, sensitivity: "base" }),
    [],
  );

  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [sortValue, setSortValue] = useState<
    | "start_desc"
    | "start_asc"
    | "name_asc"
    | "name_desc"
    | "cost_desc"
    | "cost_asc"
    | "nbv_desc"
    | "nbv_asc"
    | "status_asc"
    | "status_desc"
  >("name_asc");

  const [items, setItems] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<DisplayError | null>(null);

  const [postingAll, setPostingAll] = useState(false);
  const [postAllErr, setPostAllErr] = useState<DisplayError | null>(null);
  const [deprSummary, setDeprSummary] = useState<DepreciationSummary | null>(
    null,
  );
  const [page, setPage] = useState(1);

  const sortOptions: SortOption[] = [
    { label: t("spendingFixedAssets.purchaseNewest"), value: "start_desc" },
    { label: t("spendingFixedAssets.purchaseOldest"), value: "start_asc" },
    { label: t("spendingFixedAssets.assetAsc"), value: "name_asc" },
    { label: t("spendingFixedAssets.assetDesc"), value: "name_desc" },
    { label: t("spendingFixedAssets.costHigh"), value: "cost_desc" },
    { label: t("spendingFixedAssets.costLow"), value: "cost_asc" },
    { label: t("spendingFixedAssets.nbvHigh"), value: "nbv_desc" },
    { label: t("spendingFixedAssets.nbvLow"), value: "nbv_asc" },
    { label: t("spendingFixedAssets.statusAsc"), value: "status_asc" },
    { label: t("spendingFixedAssets.statusDesc"), value: "status_desc" },
  ];

  const newItems: MenuItem[] = [
    { label: t("spendingFixedAssets.new"), onClick: () => go("/asset-purchases/new") },
  ];

  const sortKey = sortValue.replace(/_(asc|desc)$/, "") as SortKey;
  const sortDir = sortValue.endsWith("_asc") ? "asc" : "desc";

  const handleHeaderSort = (key: SortKey) => {
    setSortValue((prev) => {
      const prevKey = prev.replace(/_(asc|desc)$/, "") as SortKey;
      const prevDir = prev.endsWith("_asc") ? "asc" : "desc";

      if (prevKey === key)
        return `${key}_${prevDir === "asc" ? "desc" : "asc"}` as any;

      const defaultDir = key === "name" || key === "status" ? "asc" : "desc";
      return `${key}_${defaultDir}` as any;
    });
  };

  const filtered = useMemo(() => {
    const q = (search || "").toLowerCase().trim();
    const from = fromDate;
    const to = toDate;

    let list = [...(items || [])].filter((it) => {
      const sd = String(it.start_date || "").slice(0, 10);
      if (from && sd && sd < from) return false;
      if (to && sd && sd > to) return false;

      if (!q) return true;

      const dispStart = fmtDate(it.start_date).toLowerCase();

      return (
        (it.name || "").toLowerCase().includes(q) ||
        (it.category || "").toLowerCase().includes(q) ||
        (it.status || "").toLowerCase().includes(q) ||
        String(it.cost ?? "").includes(q) ||
        String(it.nbv ?? "").includes(q) ||
        String(it.start_date ?? "").includes(q) ||
        dispStart.includes(q)
      );
    });

    const cmpText = (a: string, b: string) =>
      collator.compare(a || "", b || "");
    const cmpNum = (a: number, b: number) => (a || 0) - (b || 0);
    const cmpYmd = (a?: string | null, b?: string | null) => {
      const aa = String(a || "").slice(0, 10);
      const bb = String(b || "").slice(0, 10);
      return aa.localeCompare(bb);
    };

    const dir = sortDir === "asc" ? 1 : -1;

    list.sort((a, b) => {
      if (sortKey === "start") return dir * cmpYmd(a.start_date, b.start_date);
      if (sortKey === "name") return dir * cmpText(a.name, b.name);
      if (sortKey === "status") return dir * cmpText(a.status, b.status);
      if (sortKey === "cost") return dir * cmpNum(a.cost || 0, b.cost || 0);
      return dir * cmpNum(a.nbv || 0, b.nbv || 0);
    });

    return list;
  }, [items, search, fromDate, toDate, sortKey, sortDir, collator]);

  useEffect(() => {
    setPage(1);
  }, [search, fromDate, toDate, sortValue, items.length]);

  const totalFiltered = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / ROWS_PER_PAGE));

  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE),
    [filtered, page],
  );

  const fromRow = totalFiltered === 0 ? 0 : (page - 1) * ROWS_PER_PAGE + 1;
  const toRow =
    totalFiltered === 0 ? 0 : Math.min(page * ROWS_PER_PAGE, totalFiltered);

  const hasRecordableAssets = useMemo(
    () => items.some((x) => x.status === "Active"),
    [items],
  );

  const moreItems: MenuItem[] = [
    {
      label: t("directory.common.exportCsv"),
      onClick: () => {
        const headers = [
          "Purchase Date",
          "Asset",
          "Category",
          "End Date",
          "Cost",
          "NBV",
          "Status",
          "Asset ID",
        ];
        const rows = filtered.map((d) => [
          String(d.start_date || "").slice(0, 10),
          d.name || "",
          d.category || "",
          String(d.end_date || "").slice(0, 10),
          d.cost ?? "",
          d.nbv ?? "",
          d.status || "",
          d.id,
        ]);
        downloadCsv("fixed_assets.csv", headers, rows);
      },
    },
  ];

  const loadAssets = async () => {
    if (!company_id) return;
    const asOf = ymdTodayLocal();

    setLoading(true);
    setErr(null);

    try {
      const assets = await getC<FixedAsset[]>("/fixed-assets");

      const rows: Row[] = await Promise.all(
        (assets || []).map(async (a) => {
          let schedules: Schedule[] = [];
          try {
            schedules = await getC<Schedule[]>(
              `/fixed-assets/${a.id}/schedules`,
            );
          } catch {
            schedules = [];
          }

          const chosen =
            schedules.find(
              (s) => String(s.status || "").toLowerCase() === "active",
            ) ||
            schedules[0] ||
            null;

          const start_date = chosen?.start_date
            ? String(chosen.start_date).slice(0, 10)
            : null;

          const end_date =
            chosen?.start_date && chosen.useful_life_months
              ? addMonthsYmdLocal(
                  String(chosen.start_date).slice(0, 10),
                  Math.max(0, Number(chosen.useful_life_months) - 1),
                )
              : null;

          let pos: Position | null = null;
          try {
            pos = await getC<Position>(
              `/fixed-assets/${a.id}/position?as_of=${asOf}`,
            );
          } catch {
            pos = null;
          }

          const disposed = !!a.disposed;

          let status: Row["status"] = "No Schedule";
          if (disposed) status = "Disposed";
          else if (!schedules || schedules.length === 0) status = "No Schedule";
          else if (
            schedules.some(
              (s) => String(s.status || "").toLowerCase() === "active",
            )
          )
            status = "Active";
          else status = "Closed";

          return {
            id: a.id,
            name: a.name,
            category: a.asset_account_name || null,
            start_date,
            end_date,
            cost: pos ? pos.cost : null,
            nbv: pos ? pos.nbv : null,
            status,
            on_account: !!a.on_account,
            amount_due: a.amount_due ?? null,
            payment_status: a.payment_status ?? null,
          };
        }),
      );

      setItems(rows);
    } catch (e: any) {
      setErr(e?.message ? { kind: "raw", text: e.message } : { kind: "load" });
    } finally {
      setLoading(false);
    }
  };

const loadDepreciationSummary = async () => {
  if (!company_id) return;

  try {
    const summary = await getC<DepreciationSummary>(
      `/depreciations/summary?_ts=${Date.now()}`,
    );
    setDeprSummary(summary);
  } catch {
    setDeprSummary(null);
  }
};

  useEffect(() => {
    if (!company_id) return;
    loadAssets();
    loadDepreciationSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company_id]);

  const handleRecordAllDepreciation = async () => {
    if (!company_id) return;
    setPostAllErr(null);
    setPostingAll(true);
    try {
      await apiPost(`/depreciations/post-month?company_id=${company_id}`, {});
      await loadAssets();
      await loadDepreciationSummary();
    } catch (e: any) {
      if (isClosedPeriodError(e)) {
        setPostAllErr({ kind: "closed" });
        return;
      }

      const raw = e?.response?.data?.detail || e?.message;
      setPostAllErr(raw ? { kind: "raw", text: raw } : { kind: "record" });
    } finally {
      setPostingAll(false);
    }
  };

  const pagerText = (
    <span className={`${A.label} text-appleblue select-none`}>
      {t("directory.common.showing", { from: fromRow, to: toRow, total: totalFiltered })}
    </span>
  );

  const pagerBtnCls = "px-2 active:scale-100";

  return (
    <div className={`w-full ${A.body}`}>
      <ListToolbar
        maxWidthClass="max-w-6xl"
        search={search}
        onSearchChange={setSearch}
        sortValue={sortValue}
        sortOptions={sortOptions}
        onSortChange={setSortValue as any}
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={setFromDate}
        onToDateChange={setToDate}
        newItems={newItems}
        moreItems={moreItems}
        searchPlaceholder={t("spendingFixedAssets.search")}
        moreLabel={t("directory.common.more")}
        sortLabel={t("directory.common.sort")}
        fromLabel={t("sales.shared.from")}
        toLabel={t("sales.shared.to")}
        openCalendarLabel={t("sales.shared.openCalendar")}
      />

      <div className="w-full max-w-6xl mx-auto px-2 mt-3 flex flex-col items-end">
        <Button
          variant="primary"
          type="button"
          onClick={handleRecordAllDepreciation}
          disabled={loading || postingAll || !hasRecordableAssets}
        >
          {postingAll ? t("spendingFixedAssets.recording") : t("spendingFixedAssets.recordDepreciation")}
        </Button>

        <div className="mt-1 text-xs text-gray-500">
          {deprSummary?.posted_through
            ? t("spendingFixedAssets.postedThrough", { date: formatAnyDateToLocale(deprSummary.posted_through) })
            : t("spendingFixedAssets.noDepreciationPosted")}
        </div>
      </div>

      {postAllErr ? (
        <div className="w-full max-w-6xl mx-auto px-2 mt-2 text-xs text-red-500 font-medium">
          {errorText(postAllErr)}
        </div>
      ) : null}

      {loading ? (
        <div className={`text-center py-6 ${A.body} text-gray-500`}>
          {t("spendingFixedAssets.loading")}
        </div>
      ) : err ? (
        <EmptyState message={errorText(err)} heading={t("directory.common.nothingHere")} backLabel={t("directory.common.goBack")} />
      ) : filtered.length === 0 ? (
        <EmptyState
          message={t("spendingFixedAssets.empty")}
          heading={t("directory.common.nothingHere")}
          backLabel={t("directory.common.goBack")}
          showNewButton
          newButtonText={t("spendingFixedAssets.new")}
          newButtonPath={companyRoute(company_id, "/asset-purchases/new")}
        />
      ) : (
        <>
          {/* Mobile cards */}
          <div className="block md:hidden space-y-3 w-full max-w-6xl px-2 mt-3">
            {paged.map((d) => {
              const bubble = getStatusBubbleProps(d);

              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => go(`/fixed-assets/${d.id}`)}
                  className={[
                    A.card,
                    "w-full text-left px-4 py-3",
                    "transition hover:bg-gray-50 active:ring-2 active:ring-appleblue/20",
                    "focus:outline-none focus:ring-2 focus:ring-appleblueD",
                  ].join(" ")}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className={`${A.title} truncate`}>
                        {d.name || "—"}
                      </div>
                      <div className={`${A.label} truncate`}>
                        {d.category || "—"}
                      </div>
                      <div className={`${A.label} mt-0.5`}>
                        {t("spendingFixedAssets.purchasePrefix")} {fmtDate(d.start_date)}
                        {d.end_date ? ` • ${t("spendingFixedAssets.endPrefix")} ${fmtDate(d.end_date)}` : ""}
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <StatusBubble
                        label={statusLabel(d.status)}
                        bubbleType={bubble.bubbleType}
                        showDot={bubble.showDot}
                        dotColor={bubble.dotColor}
                      />
                    </div>
                  </div>

                  <div className="mt-3 flex items-end justify-between gap-3">
                    <div className={`${A.label} text-gray-500`}>
                      {t("directory.products.cost")}{" "}
                      <span
                        className={`ml-1 text-[13px] font-medium text-gray-700 ${A.num}`}
                      >
                        {d.cost == null ? "—" : money(d.cost)}
                      </span>
                    </div>

                    <div className="text-right">
                      <div className={A.label}>{t("spendingFixedAssets.nbv")}</div>
                      <div className={`${A.title} ${A.num}`}>
                        {d.nbv == null ? "—" : money(d.nbv)}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 h-4" />
                </button>
              );
            })}
          </div>

          {/* Pagination (mobile) */}
          <div className="md:hidden flex justify-between items-center mt-4 w-full max-w-6xl px-2">
            {pagerText}
            <div className="flex gap-2">
              <Button
                variant="tertiary"
                minWidth="0px"
                className={pagerBtnCls}
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                type="button"
              >
                {t("directory.common.prev")}
              </Button>
              <Button
                variant="tertiary"
                minWidth="0px"
                className={pagerBtnCls}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                type="button"
              >
                {t("directory.common.next")}
              </Button>
            </div>
          </div>

          {/* Desktop table */}
          <div className="hidden md:block w-full max-w-6xl mx-auto px-2 mt-3">
            <div className={`${A.card} overflow-hidden`}>
              <div className="overflow-x-auto">
                <table className={`w-full ${A.body} table-fixed`}>
                  <colgroup>
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "30%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "18%" }} />
                  </colgroup>

                  <thead>
                    <tr className="border-b border-gray-100">
                      <th
                        className={`${A.label} py-2 px-3 text-left font-medium select-none cursor-pointer`}
                        onClick={() => handleHeaderSort("start")}
                        title={t("spendingFixedAssets.sortPurchaseDate")}
                      >
                        {t("spendingFixedAssets.purchaseDate")}{" "}
                        {sortKey === "start" && (
                          <span className="text-gray-400">
                            {sortDir === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </th>

                      <th
                        className={`${A.label} py-2 px-3 text-left font-medium select-none cursor-pointer`}
                        onClick={() => handleHeaderSort("name")}
                        title={t("spendingFixedAssets.sortAsset")}
                      >
                        {t("spendingFixedAssets.asset")}{" "}
                        {sortKey === "name" && (
                          <span className="text-gray-400">
                            {sortDir === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </th>

                      <th
                        className={`${A.label} py-2 px-3 text-right font-medium select-none cursor-pointer tabular-nums`}
                        onClick={() => handleHeaderSort("cost")}
                        title={t("spendingFixedAssets.sortCost")}
                      >
                        {t("directory.products.cost")}{" "}
                        {sortKey === "cost" && (
                          <span className="text-gray-400">
                            {sortDir === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </th>

                      <th
                        className={`${A.label} py-2 px-3 text-right font-medium select-none cursor-pointer tabular-nums`}
                        onClick={() => handleHeaderSort("nbv")}
                        title={t("spendingFixedAssets.sortNbv")}
                      >
                        {t("spendingFixedAssets.nbv")}{" "}
                        {sortKey === "nbv" && (
                          <span className="text-gray-400">
                            {sortDir === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </th>

                      <th
                        className={`${A.label} py-2 px-3 text-left font-medium select-none cursor-pointer`}
                        onClick={() => handleHeaderSort("status")}
                        title={t("spendingFixedAssets.sortStatus")}
                      >
                        {t("sales.common.status")}{" "}
                        {sortKey === "status" && (
                          <span className="text-gray-400">
                            {sortDir === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {paged.map((d) => {
                      const bubble = getStatusBubbleProps(d);

                      return (
                        <tr
                          key={d.id}
                          className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer transition"
                          onClick={() => go(`/fixed-assets/${d.id}`)}
                          tabIndex={0}
                          onKeyDown={(e) =>
                            e.key === "Enter"
                              ? go(`/fixed-assets/${d.id}`)
                              : null
                          }
                          style={{ outline: "none" }}
                          aria-label={t("spendingFixedAssets.view", { id: d.id })}
                        >
                          <td
                            className={`py-2 px-3 whitespace-nowrap ${A.num}`}
                          >
                            {fmtDate(d.start_date)}
                          </td>

                          <td className="py-2 px-3 min-w-0">
                            <div className="min-w-0 font-medium text-gray-700 truncate">
                              {d.name || "—"}
                            </div>
                            <div className="flex items-center gap-2 min-w-0 mt-0.5">
                              <div className={`${A.label} truncate`}>
                                {d.category || "—"}
                              </div>
                            </div>
                          </td>

                          <td
                            className={`py-2 px-3 text-right font-medium whitespace-nowrap ${A.num}`}
                          >
                            {d.cost == null ? "—" : money(d.cost)}
                          </td>

                          <td
                            className={`py-2 px-3 text-right font-medium whitespace-nowrap ${A.num}`}
                          >
                            {d.nbv == null ? "—" : money(d.nbv)}
                          </td>

                          <td className="py-2 px-3">
                            <StatusBubble
                              label={statusLabel(d.status)}
                              bubbleType={bubble.bubbleType}
                              showDot={bubble.showDot}
                              dotColor={bubble.dotColor}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination (desktop) */}
              <div className="flex justify-between items-center mt-2 px-3 pb-3">
                {pagerText}
                <div className="flex gap-2">
                  <Button
                    variant="tertiary"
                    minWidth="0px"
                    className={pagerBtnCls}
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    type="button"
                  >
                    {t("directory.common.prev")}
                  </Button>
                  <Button
                    variant="tertiary"
                    minWidth="0px"
                    className={pagerBtnCls}
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    type="button"
                  >
                    {t("directory.common.next")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
